import { Form as BaseForm, Formik } from 'formik'
import { has } from 'lodash'
import PropTypes from 'prop-types'
import { isValidElement } from 'react'
import { FormattedMessage } from 'react-intl'

import Card from '@/components/Card'
import cn from '@/utilities/cn'

import Effect from './Effect'
import messages from './messages'

import './index.scss'

/**
 * @see {@link https://www.npmjs.com/package/yup}
 * @example
 * <Form
 *   buttons={
 *     <>
 *       <SecondaryButton
 *         onClick={this.onCancel}
 *         title={intl.formatMessage(messages.cancel)}
 *         type="button"
 *       />
 *       <PrimaryButton title={intl.formatMessage(messages.save)} type="submit" />
 *     </>
 *   }
 *   enableReinitialize={true}
 *   initialValues={{ someField1: 'ABC', someField2: 'DEF' }}
 *   isActionInProgress={this.isActionInProgress}
 *   onReset={this.onCancel}
 *   onSubmit={this.onSubmit}
 *   schema={
 *     object().shape({
 *       someField1: string()
 *         .required(<FormattedMessage id="RequiredError" defaultMessage="Please enter a value." />),
 *       someField2: string()
 *         .required(<FormattedMessage id="RequiredError" defaultMessage="Please enter a value." />)
 *         .oneOf(
 *           ['ABC', 'DEF', '123'],
 *           <FormattedMessage id="OneOfError" defaultMessage="Please select a valid value." />
 *         )
 *     })
 *   }
 *   title={intl.formatMessage(messages.cardTitle)}
 * >
 *   <Text
 *     description={<FormattedMessage id="Some1Description" defaultMessage="Some Text" />}
 *     label={<FormattedMessage id="Some1Label" defaultMessage="Some Label" />}
 *     name="someField1"
 *     placeholder={<FormattedMessage id="Some1Placeholder" defaultMessage="Some Placeholder" />}
 *     required
 *   />
 *
 *   <Select
 *     description={<FormattedMessage id="Some2Description" defaultMessage="Some Select" />}
 *     label={<FormattedMessage id="Some2Label" defaultMessage="Some Label" />}
 *     name="someField2"
 *     required
 *   >
 *     <Select.Option key="ABC" value="ABC">
 *       <FormattedMessage id="Some2Option.ABC" defaultMessage="Option ABC" />
 *     </Select.Option>
 *     <Select.Option key="DEF" value="DEF">
 *       <FormattedMessage id="Some2Option.DEF" defaultMessage="Option DEF" />
 *     </Select.Option>
 *     <Select.Option key="123" value="123">
 *       <FormattedMessage id="Some2Option.123" defaultMessage="Option 123" />
 *     </Select.Option>
 *   </Select>
 * </Form>
 */
const Form = ({
  actionMessage,
  buttons,
  children,
  className,
  enableReinitialize,
  initialValues,
  isActionInProgress,
  onChange,
  onReset,
  onSubmit,
  schema,
  title,
  titleToolTip,
  validate,
  validationWarning
}: any) => (
  <Formik
    enableReinitialize={enableReinitialize}
    initialValues={initialValues}
    onReset={onReset}
    onSubmit={onSubmit}
    validate={validate}
    validationSchema={schema}
  >
    {props => {
      return (
        <BaseForm autoComplete="off" className={cn('form', className)} noValidate>
          {typeof onChange === 'function' && (
            <Effect
              onChange={(values: any, formik: any) => {
                onChange(values, formik)
              }}
            />
          )}
          <Card isActionInProgress={isActionInProgress} actionMessage={actionMessage}>
            {!!title && <Card.Header title={title} titleToolTip={titleToolTip} />}
            <Card.Content>{children}</Card.Content>
            {renderButtons(buttons, props, validationWarning)}
          </Card>
        </BaseForm>
      )
    }}
  </Formik>
)

const BasicForm = ({
  children,
  className,
  enableReinitialize,
  initialValues,
  onChange,
  onReset,
  onSubmit,
  validationSchema,
  validate,
  formikRef
}: any) => (
  <Formik
    enableReinitialize={enableReinitialize}
    initialValues={initialValues}
    onReset={onReset}
    onSubmit={onSubmit}
    validate={validate}
    validationSchema={validationSchema}
    ref={formikRef}
  >
    {() => {
      return (
        <BaseForm autoComplete="off" className={cn('form', className)} noValidate>
          {typeof onChange === 'function' && (
            <Effect
              onChange={(values: any, formik: any) => {
                onChange(values, formik)
              }}
            />
          )}
          {children}
        </BaseForm>
      )
    }}
  </Formik>
)

const showValidationWarning = (touched: any, errors: any) => {
  let showValidationWarning = false
  for (const error in errors) {
    if (has(touched, error)) {
      showValidationWarning = true
      break
    }
  }
  return showValidationWarning
}

const renderButtons = (buttons: any, props: any, validationWarning: any) => {
  const wrap = (children: any) => (
    <>
      {!!validationWarning && showValidationWarning(props.touched, props.errors) && validationWarning}
      <Card.Footer>{children}</Card.Footer>
    </>
  )

  if (isValidElement(buttons)) return wrap(buttons)
  else if (!Array.isArray(buttons)) return null

  function handleClick(button: any) {
    return (e: any) => {
      if (button.type === 'submit') {
        button.secondarySubmit && props.setFieldValue('secondarySubmit', true, false)
        props.submitForm()
      }
      button.onClick && button.onClick(e, props)
    }
  }

  return wrap(
    buttons.map((button, index) => {
      if (isValidElement(button)) return button

      return (
        <button.component key={index} onClick={handleClick(button)}>
          {button.label}
        </button.component>
      )
    })
  )
}

Form.displayName = 'Form'
BasicForm.displayName = 'BasicForm'

Form.propTypes = {
  actionMessage: PropTypes.node,
  buttons: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.arrayOf(
      PropTypes.shape({
        component: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.object]),
        type: PropTypes.string,
        onClick: PropTypes.func,
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
      })
    )
  ]),
  children: PropTypes.node,
  className: PropTypes.string,
  enableReinitialize: PropTypes.bool,
  initialValues: PropTypes.object,
  isActionInProgress: PropTypes.bool,
  onChange: PropTypes.func,
  onReset: PropTypes.func,
  onSubmit: PropTypes.func,
  schema: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  titleToolTip: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  validationWarning: PropTypes.element,
  validate: PropTypes.func
}

BasicForm.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  enableReinitialize: PropTypes.bool,
  initialValues: PropTypes.object,
  onChange: PropTypes.func,
  onReset: PropTypes.func,
  onSubmit: PropTypes.func,
  validationSchema: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  formikRef: PropTypes.object,
  validate: PropTypes.func
}

Form.defaultProps = {
  actionMessage: <FormattedMessage {...messages.loading} />,
  buttons: null,
  children: null,
  className: null,
  enableReinitialize: false,
  initialValues: {},
  isActionInProgress: false,
  onChange: null,
  onReset: () => {},
  onSubmit: () => {},
  schema: null,
  title: null,
  titleToolTip: null,
  validate: () => {},
  validationWarning: null
}

BasicForm.defaultProps = {
  enableReinitialize: false,
  initialValues: {},
  onChange: () => {},
  onReset: () => {},
  onSubmit: () => {}
}

export default Form

export { BasicForm }

export { default as AutoComplete } from './AutoComplete'
export { default as DateTimePicker } from './DateTimePicker'
export { default as Checkbox } from './Checkbox'
export { default as FilterSelect } from './FilterSelect'
export { default as Radio } from './Radio/Radio'
export { default as FormattedHeader } from './FormattedHeader'
export { default as RichText } from './RichText'
export { default as Text } from './Text'
export { default as Select } from './Select'
export { default as TextWithSpellCheck } from './TextWithSpellCheck'
export { default as Textarea } from './Textarea'
export { default as yupTestContextHelper } from './yupTestContextHelper'
export { default as WorkflowDateTimePicker } from './WorkflowDateTimePicker'
